import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import ICU from 'i18next-icu';

import enCommon from '../locales/en/common.json';
import esCommon from '../locales/es/common.json';
import frCommon from '../locales/fr/common.json';
import zhCommon from '../locales/zh/common.json';
import jaCommon from '../locales/ja/common.json';
import koCommon from '../locales/ko/common.json';
import ptCommon from '../locales/pt/common.json';

export const APP_INTERFACE_TRANSLATIONS_ENABLED = false;
export const ALL_INTERFACE_LOCALES = ['en', 'es', 'fr', 'zh', 'ja', 'ko', 'pt'] as const;
export type SupportedLocale = (typeof ALL_INTERFACE_LOCALES)[number];
export const SUPPORTED_LOCALES: readonly SupportedLocale[] =
  APP_INTERFACE_TRANSLATIONS_ENABLED ? ALL_INTERFACE_LOCALES : ['en'];

// RTL locales (none in phase 1, but wired up for future ar/he additions).
const RTL_LOCALES = new Set<string>([]);

export const resources = {
  en: { common: enCommon },
  es: { common: esCommon },
  fr: { common: frCommon },
  zh: { common: zhCommon },
  ja: { common: jaCommon },
  ko: { common: koCommon },
  pt: { common: ptCommon },
} as const;

void i18n
  .use(ICU)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    ...(APP_INTERFACE_TRANSLATIONS_ENABLED ? {} : { lng: 'en' }),
    fallbackLng: 'en',
    supportedLngs: SUPPORTED_LOCALES as unknown as string[],
    ns: ['common'],
    defaultNS: 'common',
    interpolation: {
      // React already escapes; disabling avoids double-escape of user-supplied names.
      escapeValue: false,
    },
    detection: {
      order: APP_INTERFACE_TRANSLATIONS_ENABLED ? ['localStorage', 'navigator'] : [],
      lookupLocalStorage: 'hsy.locale',
      caches: APP_INTERFACE_TRANSLATIONS_ENABLED ? ['localStorage'] : [],
    },
    returnNull: false,
  });

// Keep <html lang="…" dir="…"> in sync with the active locale. Screen readers
// use `lang` to switch voice; logical CSS properties use `dir` for RTL support.
function applyDocumentLocale(lng: string) {
  if (typeof document === 'undefined') return;
  const base = APP_INTERFACE_TRANSLATIONS_ENABLED ? lng.split('-')[0] : 'en';
  document.documentElement.lang = base;
  document.documentElement.dir = RTL_LOCALES.has(base) ? 'rtl' : 'ltr';
  // CJK-specific body classes for line-height / italic adjustments (see docs/improvements/platform/08-i18n.md).
  document.documentElement.classList.toggle('i18n-zh', base === 'zh');
  document.documentElement.classList.toggle('i18n-ja', base === 'ja');
  document.documentElement.classList.toggle('i18n-ko', base === 'ko');
}

applyDocumentLocale(i18n.language);
i18n.on('languageChanged', applyDocumentLocale);

export default i18n;
